import clsx from 'clsx';
import React from 'react';
import { InView } from 'react-intersection-observer';

import IconDeepResearchText from '@components/Images/DeepResearch/IconDeepResearchText';
import IconArrow from '@components/Images/IconArrow';
import useAwarnessBarVisibility from '@hooks/useAwarnessBarVisibility';
import useElementsHeight from '@hooks/useElementsHeight';
import useEventTracking from '@hooks/useEventTracking';
import useWindowSize from '@hooks/useWindowSize';
import { deep_research_path } from '@routes';
import { ClickTarget, ImpressionElement, Page, click, impression } from '@tracking';

interface AITopBannerProps {
  currentPage: Page;
  isContentPaywalled: boolean;
  isNonProSubscriber: boolean;
  shouldShowScrolledNav: boolean;
}

const UPGRADE_TEXT = 'Upgrade to ask';
const ASK_TEXT = 'Ask';

const AITopBanner = ({
  currentPage,
  isContentPaywalled,
  isNonProSubscriber,
  shouldShowScrolledNav,
}: AITopBannerProps) => {
  useAwarnessBarVisibility(shouldShowScrolledNav);
  const { trackEvent, Track } = useEventTracking({
    location: currentPage,
  });
  const { width: windowWidth } = useWindowSize();

  useElementsHeight({
    selectors: [
      '.top-sticky-banner',
      windowWidth <= 892 && windowWidth > 767 ? '#global-nav-wrapper' : '#global-nav',
    ].filter(Boolean),
    isScrollStyle: shouldShowScrolledNav,
    dependency: [windowWidth],
    cssVariable: 'ai-banner-height',
  });

  const ctaText = isNonProSubscriber ? UPGRADE_TEXT : ASK_TEXT;

  return (
    <Track>
      <InView
        triggerOnce={true}
        onChange={(inView) => {
          inView &&
            trackEvent(impression({ impressionElement: ImpressionElement.DeepResearchBanner }));
        }}
      >
        <aside
          id="ai-research-banner"
          className={clsx(
            'relative h-full [clip-path:polygon(0_0,100%_0,100%_calc(100%-22px),calc(100%-40px)_100%,0_100%)] sm:!top-[calc(var(--ai-banner-height,0px))] sm:border-none sm:px-28 sm:py-0',
            {
              ' !top-[calc(var(--ai-banner-height,0px)+25px)] z-[-1] bg-grayscale-beige':
                [Page.Article, Page.Briefing, Page.Project].includes(currentPage) &&
                isContentPaywalled,
              '!top-0': currentPage === Page.Homepage,
            }
          )}
        >
          <a
            href={deep_research_path()}
            onClickCapture={() =>
              trackEvent(
                click({
                  clickTarget: ClickTarget.DeepResearchBanner,
                  clickText: ctaText,
                })
              )
            }
            className={clsx(
              'group mx-auto flex h-full max-w-container-lg items-center justify-end bg-grayscale-beige py-16 pr-24 [clip-path:polygon(0_0,100%_0,100%_calc(100%-22px),calc(100%-40px)_100%,0_100%)] sm:h-max sm:justify-center sm:pr-0'
            )}
            data-testid="ai-research-banner"
          >
            <h3 className="m-0 self-end font-suisse-works text-body-md font-normal sm:text-overline-lg md:text-headline-xxs">
              <span className="hidden sm:inline-block">
                Answer tough business questions, faster than ever.
              </span>{' '}
              {ctaText}
            </h3>
            <IconDeepResearchText className="mb-3 ml-12 w-[138px] sm:w-[164px] md:w-[187px]" />
            <IconArrow className="ml-24 h-16 rotate-180 text-primary-magenta transition-transform duration-200 group-hover:translate-x-2" />
          </a>
        </aside>
      </InView>
    </Track>
  );
};

export default AITopBanner;
